/**
 * 管理后台接口（仅平台 admin 角色可用；非 admin 调用会被后端 403 拒绝）。
 *
 * 对应路由（apps/api/src/routes/admin.route.ts）：
 * - GET   /admin/stats       平台概览
 * - GET   /admin/users       用户列表（keyword/role/page/pageSize）
 * - PATCH /admin/users/:id   改角色 / 禁用或启用
 * - GET   /admin/audit-logs  审计日志（action/userId/page/pageSize）
 */
import { api, queryString } from './client.js';
import type {
  AdminAuditLogDto,
  AdminStatsDto,
  AdminUserDto,
  PageDto,
} from './types.js';
import type { UserRole } from '../store/session.js';

// 用 type 而非 interface：queryString() 的参数要求隐式索引签名（interface 不满足）
export type AdminUsersQuery = {
  /** 关键词：邮箱或邀请 ID（CM-XXXXXX）模糊匹配 */
  keyword?: string;
  role?: UserRole;
  page?: number;
  pageSize?: number;
};

export type AdminAuditQuery = {
  /** action 模糊过滤，如 user. / team. */
  action?: string;
  userId?: string;
  page?: number;
  pageSize?: number;
};

export interface AdminUserUpdate {
  role?: UserRole;
  /** true=禁用（连坐撤销会话），false=启用 */
  disabled?: boolean;
}

export const adminApi = {
  stats: (): Promise<AdminStatsDto> => api.get<AdminStatsDto>('/admin/stats'),

  users: (query: AdminUsersQuery = {}): Promise<PageDto<AdminUserDto>> =>
    api.get<PageDto<AdminUserDto>>(`/admin/users${queryString(query)}`),

  updateUser: (userId: string, input: AdminUserUpdate): Promise<AdminUserDto> =>
    api.patch<AdminUserDto>(`/admin/users/${encodeURIComponent(userId)}`, input),

  auditLogs: (query: AdminAuditQuery = {}): Promise<PageDto<AdminAuditLogDto>> =>
    api.get<PageDto<AdminAuditLogDto>>(`/admin/audit-logs${queryString(query)}`),
};
